/**
 * "Your draft" — the member's unfinished discussion draft shown in their profile (the hub
 * Overview and the `/profile` fallback), and the writer's "where is my draft" line. Pure: the
 * card is `components/community/DraftCard.tsx`, the writer's status `discussions/writer/Writer`.
 *
 * The draft is this browser's only (`draftStorage`, no server drafts — contract 2026-10-04), so
 * every word here says "this device" in plain language; the card reads storage after mount only
 * (the hub and `/profile` HTML hold nothing of the reader).
 */
import { withSlash } from 'lib/dealhunt/url'
import { twoDigits } from './model'
import {
  draftSavedClock,
  draftSummary,
  kindCopy,
  savedAgo,
  type WriterDraft,
} from './discussions-writer'

/** The card's anchor: `#draft` on the hub Overview and on `/profile`. */
export const DRAFT_ANCHOR = 'draft'

/** The handle-less profile page; it forwards a member with a hub page to the hub's `#draft`
 *  (`profileRoute`). */
export const PROFILE_PATH = '/profile'

export const DRAFT_COPY = {
  title: 'Your draft',
  untitled: 'Untitled draft',
  /** The plain-language "where": no storage jargon (one device, one draft). */
  deviceNote:
    'Saved on this device only. You won’t see it on another phone or computer, and clearing your browser data removes it.',
  continue: 'Continue writing',
  delete: 'Delete draft',
  confirm: 'Delete this draft? This can’t be undone.',
  confirmYes: 'Yes, delete',
  confirmNo: 'Cancel',
  deleted: 'Draft deleted.',
  /** The writer's saved status, new post only (then " · HH:MM" and the profile link). */
  savedHere: 'Draft saved on this device',
  findIt: 'Find it in your profile',
} as const

export interface DraftCardSummary {
  kindLabel: string
  title: string
  /** The first line of the post's text, trimmed; null when it has none. */
  excerpt: string | null
  photos: number
  gear: number
  savedAt: number | null
}

const EXCERPT_MAX = 200

function firstLine(text: string): string | null {
  const line = text
    .split('\n')
    .map(l => l.trim())
    .find(Boolean)
  return line ? line.slice(0, EXCERPT_MAX) : null
}

/** What the card shows of a stored draft — null when there is none worth naming (blank). Kind,
 *  title and time are `draftSummary`'s (the Discussions rail's line), so both say the same. */
export function draftCardSummary(draft: WriterDraft | null): DraftCardSummary | null {
  const summary = draftSummary(draft)
  if (!draft || !summary) {
    return null
  }
  let excerpt: string | null = null
  for (const b of draft.blocks) {
    if (b.type === 'text') {
      excerpt = firstLine(b.body)
    } else if (b.type === 'heading') {
      excerpt = firstLine(b.text)
    }
    if (excerpt) {
      break
    }
  }
  const photos = draft.blocks.reduce((n, b) => n + (b.type === 'images' ? b.images.length : 0), 0)
  return {
    kindLabel: kindCopy(summary.kind).label,
    title: summary.title ?? DRAFT_COPY.untitled,
    excerpt,
    photos,
    gear: draft.gear.length,
    savedAt: summary.savedAt,
  }
}

/** "2 photos · 1 linked gear" — null when the draft has neither. */
export function draftCountsLine(s: Pick<DraftCardSummary, 'photos' | 'gear'>): string | null {
  const parts: string[] = []
  if (s.photos > 0) {
    parts.push(`${s.photos} ${s.photos === 1 ? 'photo' : 'photos'}`)
  }
  if (s.gear > 0) {
    parts.push(`${s.gear} linked gear`)
  }
  return parts.length ? parts.join(' · ') : null
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const DAY_MS = 24 * 60 * 60 * 1000

/** "Last saved 5 min ago" within a day, else "Last saved Oct 3, 14:05" (the reader's local
 *  time — browser-only, like the draft); null for an older stored draft with no time. */
export function draftLastSavedLine(savedAt: number | null, now: number): string | null {
  if (savedAt === null) {
    return null
  }
  if (now - savedAt < DAY_MS) {
    // "saved 5 min ago": the restore bar's own wording (`savedAgo`), one set of thresholds.
    return `Last ${savedAgo(savedAt, now)}`
  }
  const at = new Date(savedAt)
  return `Last saved ${MONTHS[at.getMonth()]} ${at.getDate()}, ${twoDigits(
    at.getHours()
  )}:${twoDigits(at.getMinutes())}`
}

/**
 * Where the writer's "Find it in your profile" goes: always `/profile#draft`. It renders the card
 * for a reader without a hub page and forwards a member to their hub Overview's `#draft`
 * (`profileRoute`, which checks `/me`). Never a hub URL from a remembered handle: a member who has
 * since hidden their page (or renamed) would land on a 404.
 */
export function draftHomeHref(trailingSlash: boolean): string {
  return `${withSlash(PROFILE_PATH, trailingSlash)}#${DRAFT_ANCHOR}`
}

/** The card's own steps: shown → confirm (inline, no browser dialog) → deleted, or back. */
export type DraftCardStep = 'shown' | 'confirm' | 'deleted'
export type DraftCardAction = 'ask' | 'cancel' | 'confirm'

export function draftCardStep(step: DraftCardStep, action: DraftCardAction): DraftCardStep {
  if (step === 'shown' && action === 'ask') {
    return 'confirm'
  }
  if (step === 'confirm' && action === 'confirm') {
    return 'deleted'
  }
  if (step === 'confirm' && action === 'cancel') {
    return 'shown'
  }
  return step
}

/** The writer's saved status (desktop line): the "where" for a NEW post's draft only — the edit
 *  view keeps no local draft, so it never says one is saved. `time` is its own piece so a narrow
 *  desktop bar can drop it and keep the sentence and the link. */
export function writerSavedStatus(s: {
  edit: boolean
  saveState: 'idle' | 'saved' | 'failed'
  savedAt: number | null
}): { line: string; time: string | null; findIt: boolean } | null {
  if (s.edit) {
    return null
  }
  if (s.saveState === 'failed') {
    return { line: 'This browser blocks saving drafts', time: null, findIt: false }
  }
  if (s.saveState === 'saved' && s.savedAt !== null) {
    return { line: DRAFT_COPY.savedHere, time: draftSavedClock(s.savedAt), findIt: true }
  }
  return null
}
